import React, { useState } from 'react';
import { Alert, Button, Image, Input, message, Popconfirm, Select, Space, Table, Tag } from 'antd';
import { getMealPlannerCategories, setMealPlannerStatus } from '@/Axios/api/mealPlanner';
import { MealPlannerCategoryRow, MealPlannerFilters } from '@/Interfaces/I_mealPlanner';
import Details from './Details';
import useMealPlannerList from './useMealPlannerList';

export default function Categories() {
  const [filters, setFilters] = useState<MealPlannerFilters>({ page: 0, size: 24 });
  const [revision, setRevision] = useState(0);
  const [detailId, setDetailId] = useState<string>();
  const [busyId, setBusyId] = useState<string>();
  const { data, loading, error } = useMealPlannerList(getMealPlannerCategories, filters, revision);
  const toggle = async (row: MealPlannerCategoryRow) => {
    const id = row.category.categoryId;
    setBusyId(id);
    try {
      await setMealPlannerStatus('categories', id, row.category.disabled === false);
      message.success('Category status updated');
      setDetailId(undefined);
    } catch {
      message.error('Update could not be confirmed. Refresh to check the current status.');
    } finally {
      setBusyId(undefined);
      setRevision((value) => value + 1);
    }
  };
  return (
    <>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Category ID or name"
          maxLength={100}
          allowClear
          data-testid="meal-planner-category-search"
          onSearch={(search) =>
            setFilters((value) => ({ ...value, page: 0, search: search || undefined }))
          }
        />
        <Select
          placeholder="All statuses"
          allowClear
          style={{ width: 160 }}
          data-testid="meal-planner-category-status-filter"
          options={[
            { value: 'enabled', label: 'Enabled' },
            { value: 'disabled', label: 'Disabled' },
          ]}
          onChange={(status?: string) =>
            setFilters((value) => ({
              ...value,
              page: 0,
              disabled: status ? status === 'disabled' : undefined,
            }))
          }
        />
        <Button
          onClick={() => setRevision((value) => value + 1)}
          data-testid="meal-planner-category-refresh"
        >
          Refresh
        </Button>
      </Space>
      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}
      <Table<MealPlannerCategoryRow>
        rowKey={(row) => row.category.categoryId}
        dataSource={data?.items || []}
        loading={loading}
        pagination={{
          current: filters.page + 1,
          pageSize: filters.size,
          total: data?.pagination.totalElements || 0,
          showSizeChanger: true,
          pageSizeOptions: [24, 48, 100],
          onChange: (page, size) =>
            setFilters((value) => ({ ...value, page: size !== value.size ? 0 : page - 1, size })),
        }}
        columns={[
          { title: 'ID', render: (_, row) => row.category.categoryId },
          {
            title: 'Icon',
            render: (_, row) =>
              row.category.iconUrl ? <Image width={50} src={row.category.iconUrl} /> : '—',
          },
          {
            title: 'Name',
            render: (_, row) => (
              <Button
                type="link"
                onClick={() => setDetailId(row.category.categoryId)}
                data-testid={`meal-planner-category-detail-${row.category.categoryId}`}
              >
                {row.category.name || row.category.categoryId}
              </Button>
            ),
          },
          { title: 'Type', render: (_, row) => row.category.type || '—' },
          { title: 'Assigned', dataIndex: 'totalRecipeCount' },
          { title: 'Active', dataIndex: 'activeRecipeCount' },
          {
            title: 'Status',
            render: (_, row) => (
              <Tag color={row.category.disabled === false ? 'green' : undefined}>
                {row.category.disabled == null
                  ? 'Unknown'
                  : row.category.disabled
                  ? 'Disabled'
                  : 'Enabled'}
              </Tag>
            ),
          },
          {
            title: 'Action',
            render: (_, row) => (
              <Popconfirm
                title={`${row.category.disabled === false ? 'Disable' : 'Enable'} this category?`}
                onConfirm={() => toggle(row)}
                disabled={!!busyId}
              >
                <Button
                  loading={busyId === row.category.categoryId}
                  disabled={!!busyId || loading}
                  data-testid={`meal-planner-category-toggle-${row.category.categoryId}`}
                >
                  {row.category.disabled === false ? 'Disable' : 'Enable'}
                </Button>
              </Popconfirm>
            ),
          },
        ]}
      />
      <Details kind="categories" id={detailId} onClose={() => setDetailId(undefined)} />
    </>
  );
}
